// PointerEvent ベースの統合入力ハンドラ。
// マウス / タッチ / タッチペンを同一に扱い、現在のツールに応じて動作を切り替える。
//
// - tool === "draw-pen": pointerdown でストローク開始、pointermove で増分、pointerup で確定
// - tool === "place-*": pointerdown でオブジェクトを配置（仮実装：場所のみ記録）
// - tool === "idle" の空白部 pointerdown: パン開始
// - tool === "idle" のオブジェクト pointerdown: transform 開始（実装は selection-overlay 側）

import { useEffect, useRef, type RefObject } from "react";
import { useCanvasStore } from "../store/canvas-store";
import { loadPenThickness, PEN_COLOR_HEX, PEN_THICKNESS_PIXELS } from "../../../lib/pen-preference";
import type { StrokePoint } from "../../../lib/pen-stroke";
import { screenToStage } from "../../../lib/canvas-coordinates";

interface PenLayerHandle {
  readonly canvas: HTMLCanvasElement;
}

interface Handlers {
  /** ストローク確定時に呼ばれる。サーバへ pen.stroke.commit op を送る用途。 */
  readonly onStrokeCommit: (params: {
    points: readonly StrokePoint[];
    color: string;
    thickness: number;
  }) => void;
  /** 付箋・図形・テキストの配置確定時に呼ばれる。 */
  readonly onPlaceObject: (params: { stagePoint: { x: number; y: number } }) => void;
}

interface Options {
  readonly stageRef: RefObject<HTMLElement | null>;
  readonly penLayerRef: RefObject<PenLayerHandle | null>;
}

export function usePointerMode(options: Options, handlers: Handlers): void {
  const stateRef = useRef<{
    drawing: boolean;
    panning: boolean;
    panStart: { x: number; y: number; panX: number; panY: number } | null;
    points: StrokePoint[];
  }>({ drawing: false, panning: false, panStart: null, points: [] });

  useEffect(() => {
    const container = options.stageRef.current;
    if (!container) return undefined;

    const onPointerDown = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const tool = useCanvasStore.getState().tool;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore.getState().setLastPointerStagePosition(stagePoint);

      if (tool === "draw-pen") {
        stateRef.current.drawing = true;
        stateRef.current.points = [
          { x: stagePoint.x, y: stagePoint.y, pressure: pressureOf(event) },
        ];
        container.setPointerCapture(event.pointerId);
        return;
      }

      if (tool === "place-sticky" || tool === "place-shape" || tool === "place-text") {
        handlers.onPlaceObject({ stagePoint });
        useCanvasStore.getState().setTool("idle");
        return;
      }

      if (tool === "idle" && event.target === container) {
        // 空白部のクリック: 既存の選択を解除する
        useCanvasStore.getState().setSelectedObjectIds([]);
        // 空白部のドラッグ: パン
        stateRef.current.panning = true;
        stateRef.current.panStart = {
          x: event.clientX,
          y: event.clientY,
          panX: view.panX,
          panY: view.panY,
        };
        useCanvasStore.getState().setTool("panning");
        container.setPointerCapture(event.pointerId);
      }
    };

    const onPointerMove = (event: PointerEvent): void => {
      const view = useCanvasStore.getState().view;
      const stagePoint = screenToStage({ x: event.clientX, y: event.clientY }, view);
      useCanvasStore.getState().setLastPointerStagePosition(stagePoint);

      if (stateRef.current.drawing) {
        stateRef.current.points.push({
          x: stagePoint.x,
          y: stagePoint.y,
          pressure: pressureOf(event),
        });
        // 入力中ストロークは Canvas2D に増分描画する（暫定：全描画 redraw でなく
        // 直前ポイントから現在ポイントまでをライン描画しても十分滑らか）
        const handle = options.penLayerRef.current;
        if (handle) {
          const ctx = handle.canvas.getContext("2d");
          if (ctx) {
            const color = PEN_COLOR_HEX[useCanvasStore.getState().penColor];
            const thickness = PEN_THICKNESS_PIXELS[loadPenThickness()];
            ctx.save();
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.strokeStyle = color;
            ctx.lineWidth = thickness;
            const points = stateRef.current.points;
            const last = points[points.length - 2];
            const cur = points[points.length - 1];
            if (last && cur) {
              ctx.beginPath();
              ctx.moveTo(last.x, last.y);
              ctx.lineTo(cur.x, cur.y);
              ctx.stroke();
            }
            ctx.restore();
          }
        }
        return;
      }

      if (stateRef.current.panning && stateRef.current.panStart) {
        const start = stateRef.current.panStart;
        useCanvasStore.getState().setView({
          ...view,
          panX: start.panX + (event.clientX - start.x),
          panY: start.panY + (event.clientY - start.y),
        });
      }
    };

    const onPointerUp = (event: PointerEvent): void => {
      if (stateRef.current.drawing) {
        const points = stateRef.current.points;
        stateRef.current.drawing = false;
        if (points.length > 1) {
          const color = PEN_COLOR_HEX[useCanvasStore.getState().penColor];
          const thickness = PEN_THICKNESS_PIXELS[loadPenThickness()];
          handlers.onStrokeCommit({ points, color, thickness });
          // 入力中専用レイヤは、確定後にオブジェクトレイヤへ描画されるため消去する。
          const handle = options.penLayerRef.current;
          if (handle) {
            const ctx = handle.canvas.getContext("2d");
            ctx?.clearRect(0, 0, handle.canvas.width, handle.canvas.height);
          }
        }
        stateRef.current.points = [];
      }
      if (stateRef.current.panning) {
        stateRef.current.panning = false;
        stateRef.current.panStart = null;
        useCanvasStore.getState().setTool("idle");
      }
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointercancel", onPointerUp);
    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointercancel", onPointerUp);
    };
  }, [handlers, options.penLayerRef, options.stageRef]);
}

function pressureOf(event: PointerEvent): number {
  // ペンデバイスのみ筆圧を採用、それ以外は均一な太さで描画
  if (event.pointerType === "pen" && event.pressure > 0) {
    return event.pressure;
  }
  return 0.5;
}
